import React from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, Clock, MessageCircle, ArrowLeft, Mail, Phone } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FloatingActions from '../components/FloatingActions';
import SEO from '../components/SEO';

/**
 * RefundPolicyPage — Formal refund policy for ViratAstro (Vivek Mudgal).
 *
 * Values on this page are the CURRENT, ACTUAL refund terms already communicated
 * on /booking (see BookingPage.js FAQ block) and inside the Poocho Guruji T&C
 * checkbox. No new/invented policy — this page consolidates what already exists
 * across the site into one canonical URL, per SEO audit issue N8 (P1).
 */
export default function RefundPolicyPage() {
  return (
    <div className="min-h-screen bg-background text-text-main" data-testid="refund-policy-page">
      <SEO
        title="Refund Policy — ViratAstro | Acharya Vivek Mudgal"
        description="ViratAstro refund policy for Poocho Guruji Quick (₹99), Deep Analysis (₹299), Poocho Guruji Premium (₹999) and Personal Consultation (₹5,100). Razorpay-secured payments, transparent tier-wise refund terms."
        path="/refund-policy"
        breadcrumbs={[
          { name: 'होम', path: '/' },
          { name: 'Refund Policy', path: '/refund-policy' },
        ]}
        keywords="refund policy, viratastro refund, razorpay refund, poocho guruji refund, astrology consultation refund"
      />
      <Header />
      <FloatingActions />

      <section className="pt-28 pb-10 bg-gradient-to-b from-[#1a0710] via-[#0f0508] to-[#0a0410]">
        <div className="container-custom max-w-4xl">
          <Link to="/" className="inline-flex items-center gap-2 text-gold hover:gap-3 transition-all mb-6 text-sm" data-testid="refund-back-home">
            <ArrowLeft className="w-4 h-4" /> होम पर वापस
          </Link>
          <span className="block text-xs tracking-[0.3em] uppercase text-gold font-semibold mb-3">Refund &amp; Cancellation Policy</span>
          <h1 className="font-heading text-3xl sm:text-5xl text-text-main mb-4 leading-tight">Refund Policy — पारदर्शी और उचित</h1>
          <p className="text-text-muted max-w-2xl leading-relaxed">
            ViratAstro (Acharya Vivek Mudgal) पर हर payment RBI-approved Razorpay gateway से secure है। नीचे प्रत्येक tier की वास्तविक refund शर्तें बताई गई हैं — जो हमारी booking page एवं T&amp;C में पहले से लागू हैं।
          </p>
        </div>
      </section>

      <section className="py-12">
        <div className="container-custom max-w-4xl space-y-6">
          {/* Quick summary strip */}
          <div className="grid sm:grid-cols-3 gap-3 mb-4">
            <div className="p-4 rounded-xl bg-gold/5 border border-gold/25 flex items-center gap-3" data-testid="refund-strip-secure">
              <ShieldCheck className="w-5 h-5 text-emerald-400 shrink-0" />
              <span className="text-sm">100% Razorpay Secured</span>
            </div>
            <div className="p-4 rounded-xl bg-gold/5 border border-gold/25 flex items-center gap-3" data-testid="refund-strip-tat">
              <Clock className="w-5 h-5 text-gold shrink-0" />
              <span className="text-sm">5–7 working days credit</span>
            </div>
            <div className="p-4 rounded-xl bg-gold/5 border border-gold/25 flex items-center gap-3" data-testid="refund-strip-support">
              <MessageCircle className="w-5 h-5 text-green-400 shrink-0" />
              <span className="text-sm">WhatsApp पर तुरंत सहायता</span>
            </div>
          </div>

          {/* ₹99 Poocho Guruji Quick */}
          <div className="rounded-2xl bg-[#0a0820]/70 border border-gold/25 p-6" data-testid="refund-tier-quick-99">
            <div className="flex flex-wrap items-baseline gap-3 mb-2">
              <h2 className="font-heading text-2xl text-gold">पूछो Guruji · Quick</h2>
              <span className="text-3xl font-heading font-bold text-gold">₹99</span>
            </div>
            <p className="text-text-muted text-sm mb-3">एक प्रश्न का व्यक्तिगत वैदिक उत्तर — WhatsApp/Email पर लगभग 10 मिनट में delivery।</p>
            <ul className="list-disc pl-5 space-y-2 text-text-main text-[15px] leading-relaxed">
              <li><strong className="text-gold">Full refund</strong> — यदि payment के 2 घंटे के भीतर आपको उत्तर <em>generate</em> नहीं होता (technical failure / server-side error) और आप WhatsApp <a href="https://wa.me/919311369729" className="text-gold underline">+91-9311369729</a> पर contact करते हैं।</li>
              <li><strong className="text-gold">Full refund</strong> — यदि AI answer कभी deliver नहीं हो पाया (WhatsApp/email दोनों fail) और WhatsApp पर 24 घंटे के भीतर आप शिकायत करते हैं।</li>
              <li>उत्तर मिलने के बाद (उत्तर की गुणवत्ता / ग्रह-फल-असहमति के आधार पर) refund नहीं होगा — क्योंकि सेवा deliver हो चुकी है। पर आप उसी session_id पर WhatsApp पर आचार्य जी से एक clarification पूछ सकते हैं।</li>
            </ul>
          </div>

          {/* ₹299 Deep Analysis */}
          <div className="rounded-2xl bg-[#0a0820]/70 border border-gold/25 p-6" data-testid="refund-tier-deep-299">
            <div className="flex flex-wrap items-baseline gap-3 mb-2">
              <h2 className="font-heading text-2xl text-gold">Deep Analysis</h2>
              <span className="text-3xl font-heading font-bold text-gold">₹299</span>
            </div>
            <p className="text-text-muted text-sm mb-3">3 प्रश्नों का विस्तृत Parashari उत्तर + Dasha विश्लेषण + PDF रिपोर्ट + WhatsApp — approx. 24 घंटे में।</p>
            <ul className="list-disc pl-5 space-y-2 text-text-main text-[15px] leading-relaxed">
              <li><strong className="text-gold">Full refund</strong> — अगर PDF report 48 घंटे के भीतर आपके WhatsApp/email पर नहीं पहुँचती।</li>
              <li>Report deliver होने के बाद refund नहीं होगा। पर एक free follow-up doubt-clarification session मिलेगा — जिसकी booking WhatsApp पर कीजिये।</li>
            </ul>
          </div>

          {/* ₹999 Premium */}
          <div className="rounded-2xl bg-[#0a0820]/70 border border-gold/25 p-6" data-testid="refund-tier-premium-999">
            <div className="flex flex-wrap items-baseline gap-3 mb-2">
              <h2 className="font-heading text-2xl text-gold">पूछो Guruji · Premium (Monthly)</h2>
              <span className="text-3xl font-heading font-bold text-gold">₹999</span>
            </div>
            <p className="text-text-muted text-sm mb-3">30 दिन तक प्रतिदिन 1 व्यक्तिगत Kundli-आधारित AI मार्गदर्शन।</p>
            <ul className="list-disc pl-5 space-y-2 text-text-main text-[15px] leading-relaxed">
              <li><strong className="text-gold">Full refund</strong> — पहले 24 घंटे के भीतर अगर आपको कोई भी AI मार्गदर्शन नहीं मिला।</li>
              <li>Activation के बाद pro-rata refund संभव नहीं — पर 30-दिन के दौरान कभी भी WhatsApp पर WhatsApp असंतुष्टि दर्ज कराने पर आचार्य जी स्वयं आपकी कुंडली review करके एक free personalised note WhatsApp पर भेजेंगे।</li>
            </ul>
          </div>

          {/* ₹5,100 Personal */}
          <div className="rounded-2xl bg-[#0a0820]/70 border border-gold/25 p-6" data-testid="refund-tier-personal-5100">
            <div className="flex flex-wrap items-baseline gap-3 mb-2">
              <h2 className="font-heading text-2xl text-gold">Personal Consultation</h2>
              <span className="text-3xl font-heading font-bold text-gold">₹5,100</span>
            </div>
            <p className="text-text-muted text-sm mb-3">90 मिनट In-person (Faridabad) / Video call — Complete life reading + follow-up support।</p>
            <ul className="list-disc pl-5 space-y-2 text-text-main text-[15px] leading-relaxed">
              <li><strong className="text-gold">100% refund</strong> — session start होने से पहले cancel करने पर पूर्ण refund।</li>
              <li>Session शुरू होने के बाद refund नहीं होगा। पर reschedule की सुविधा — WhatsApp पर 48 घंटे पहले सूचित करने पर उपलब्ध है।</li>
            </ul>
          </div>

          {/* Procedure */}
          <div className="rounded-2xl bg-gradient-to-br from-amber-950/30 to-purple-950/30 border border-gold/40 p-6" data-testid="refund-procedure">
            <h2 className="font-heading text-2xl text-gold mb-3">Refund कैसे request करें?</h2>
            <ol className="list-decimal pl-5 space-y-2 text-text-main text-[15px] leading-relaxed">
              <li>अपना <strong>Razorpay Payment ID</strong> (जो payment के तुरंत बाद WhatsApp/screen पर दिखता है, format: <code className="text-gold">pay_XXXXXXXX</code>) copy कीजिए।</li>
              <li>WhatsApp <a href="https://wa.me/919311369729?text=Refund%20Request%20%E2%80%94%20Payment%20ID%3A%20" target="_blank" rel="noopener noreferrer" className="text-gold underline">+91-9311369729</a> पर Payment ID + तिथि + issue लिखकर भेजिए।</li>
              <li>हम 24 घंटे के भीतर आपसे confirm करेंगे। Approved होने पर refund Razorpay के माध्यम से आपके source-account (UPI / Card / Netbanking) पर <strong>5–7 working days</strong> में credit होगा।</li>
              <li>Chargeback / bank dispute से पहले please WhatsApp पर हमसे संपर्क करें — हम हमेशा सीधे बात करके solution निकालते हैं।</li>
            </ol>
          </div>

          {/* Contact */}
          <div className="rounded-2xl bg-[#0a0820]/70 border border-gold/25 p-6" data-testid="refund-contact">
            <h2 className="font-heading text-xl text-gold mb-3">सम्पर्क</h2>
            <div className="grid sm:grid-cols-2 gap-3 text-sm text-text-main">
              <a href="https://wa.me/919311369729" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-green-400" data-testid="refund-contact-whatsapp">
                <MessageCircle className="w-4 h-4 text-green-400" /> +91 93113 69729 (WhatsApp)
              </a>
              <a href="tel:+919311369729" className="flex items-center gap-2 hover:text-gold" data-testid="refund-contact-phone">
                <Phone className="w-4 h-4 text-gold" /> +91 93113 69729
              </a>
              <a href="mailto:vivek@viratastro.cloud" className="flex items-center gap-2 hover:text-gold sm:col-span-2" data-testid="refund-contact-email">
                <Mail className="w-4 h-4 text-gold" /> vivek@viratastro.cloud
              </a>
            </div>
            <p className="text-xs text-text-muted mt-3">Address: PB-77 GF Parklands Pride, Sector 77, Faridabad, Haryana</p>
          </div>

          <div className="text-center pt-2">
            <p className="text-[11px] text-text-muted/70 italic">Last updated: Feb 2026 · यह policy ViratAstro के सभी पेमेंट्स पर लागू होती है।</p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
